import { useCallback, useEffect, useRef, useState } from 'react';

import { DicomImportPipeline } from '@/lib/dicom';
import type { DicomImportResult, DicomMetadata, ProgressUpdate, SeriesInfo } from '@/lib/dicom';

/**
 * Discrete states of the browser DICOM import flow.
 *  - idle: no folder loaded yet (initial / post-reset).
 *  - parsing: worker is enumerating DICOM files & series.
 *  - series-select: more than one series found, awaiting user choice.
 *  - reconstructing: volume reconstruction + preview + hash in progress.
 *  - ready: a DicomImportResult is available on `result`.
 *  - error: something failed; details on `error`.
 */
export type DicomImportState = 'idle' | 'parsing' | 'series-select' | 'reconstructing' | 'ready' | 'error';

export interface UseDicomImportOptions {
    /**
     * Override the pipeline factory. Tests inject a stub here; default
     * spawns the real Web Worker via `new DicomImportPipeline()`.
     */
    pipelineFactory?: () => DicomImportPipeline;
}

export interface UseDicomImportResult {
    state: DicomImportState;
    progress: ProgressUpdate | null;
    seriesList: SeriesInfo[];
    result: DicomImportResult | null;
    metadata: DicomMetadata | null;
    error: Error | null;

    /** Start parsing a folder; auto-selects when only one series is found. */
    start: (files: File[]) => Promise<void>;
    /** Select a specific series; only valid in `series-select`. */
    selectSeries: (seriesInstanceUid: string) => Promise<void>;
    /** Reset to `idle` and discard any in-flight work. */
    reset: () => void;
}

/**
 * React hook around `DicomImportPipeline`.
 *
 * Owns the pipeline lifecycle (created lazily on first `start`, disposed
 * on unmount), drives the parse → optional series-pick → reconstruct
 * state machine, and surfaces the latest progress update for the UI.
 *
 * A single series in the folder is auto-selected and reconstructed
 * without user interaction; multiple series pause in `series-select`
 * for the caller's UI to pick one via `selectSeries`.
 */
export function useDicomImport(options: UseDicomImportOptions = {}): UseDicomImportResult {
    const [state, setState] = useState<DicomImportState>('idle');
    const [progress, setProgress] = useState<ProgressUpdate | null>(null);
    const [seriesList, setSeriesList] = useState<SeriesInfo[]>([]);
    const [result, setResult] = useState<DicomImportResult | null>(null);
    const [metadata, setMetadata] = useState<DicomMetadata | null>(null);
    const [error, setError] = useState<Error | null>(null);

    const pipelineRef = useRef<DicomImportPipeline | null>(null);
    const unsubscribeRef = useRef<(() => void) | null>(null);
    const factoryRef = useRef(options.pipelineFactory);
    factoryRef.current = options.pipelineFactory;

    const ensurePipeline = useCallback((): DicomImportPipeline => {
        if (!pipelineRef.current) {
            const factory = factoryRef.current ?? (() => new DicomImportPipeline());
            const pipeline = factory();
            unsubscribeRef.current = pipeline.onProgress(setProgress);
            pipelineRef.current = pipeline;
        }
        return pipelineRef.current;
    }, []);

    const disposePipeline = useCallback(() => {
        unsubscribeRef.current?.();
        unsubscribeRef.current = null;
        pipelineRef.current?.dispose();
        pipelineRef.current = null;
    }, []);

    useEffect(() => {
        return () => {
            disposePipeline();
        };
    }, [disposePipeline]);

    const reset = useCallback(() => {
        disposePipeline();
        setState('idle');
        setProgress(null);
        setSeriesList([]);
        setResult(null);
        setMetadata(null);
        setError(null);
    }, [disposePipeline]);

    const reconstruct = useCallback(async (pipeline: DicomImportPipeline): Promise<void> => {
        setState('reconstructing');
        const reconstructionResult = await pipeline.reconstruct();
        setResult(reconstructionResult);
        setMetadata(reconstructionResult.metadata);
        setState('ready');
    }, []);

    const start = useCallback(
        async (files: File[]): Promise<void> => {
            setError(null);
            setResult(null);
            setMetadata(null);
            setSeriesList([]);
            setState('parsing');
            try {
                const pipeline = ensurePipeline();
                const series = await pipeline.loadFolder(files);
                if (series.length === 0) {
                    throw new Error('No DICOM series were found in the selected folder.');
                }
                if (series.length === 1) {
                    const onlyMetadata = await pipeline.selectSeries(series[0].seriesInstanceUid);
                    setMetadata(onlyMetadata);
                    await reconstruct(pipeline);
                    return;
                }
                setSeriesList(series);
                setState('series-select');
            } catch (err) {
                setError(err instanceof Error ? err : new Error(String(err)));
                setState('error');
            }
        },
        [ensurePipeline, reconstruct]
    );

    const selectSeries = useCallback(
        async (seriesInstanceUid: string): Promise<void> => {
            const pipeline = pipelineRef.current;
            if (!pipeline) {
                setError(new Error('selectSeries called before start.'));
                setState('error');
                return;
            }
            try {
                const meta = await pipeline.selectSeries(seriesInstanceUid);
                setMetadata(meta);
                await reconstruct(pipeline);
            } catch (err) {
                setError(err instanceof Error ? err : new Error(String(err)));
                setState('error');
            }
        },
        [reconstruct]
    );

    return {
        state,
        progress,
        seriesList,
        result,
        metadata,
        error,
        start,
        selectSeries,
        reset,
    };
}
